import React from 'react';
import { Bell, CheckCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../context/NotificationsContext';
import { useLocalization } from '../context/LocalizationContext';
import { cacheUtils } from '../utils/cache';

export const NotificationBell = () => {
  const navigate = useNavigate();
  const { locale, t } = useLocalization();
  const { notifications, unreadCount, loading, markAsRead, markAllAsRead, refreshNotifications } = useNotifications();
  const [open, setOpen] = React.useState(false);
  const panelRef = React.useRef(null);
  const previewItems = notifications.slice(0, 4);

  React.useEffect(() => {
    if (open) {
      void refreshNotifications({ silent: true });
    }
  }, [open, refreshNotifications]);

  React.useEffect(() => {
    if (!open) return undefined;

    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  const openNotification = (notification) => {
    void markAsRead(notification.id);
    setOpen(false);
    if (notification.actionPath) {
      navigate(notification.actionPath);
      return;
    }
    navigate('/notifications');
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="relative inline-flex h-10 w-10 items-center justify-center rounded-full text-slate-700 transition-colors hover:bg-primary-50 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white"
        aria-label={t('notifications.openBell')}
      >
        <Bell size={19} />
        {unreadCount > 0 && (
          <span className="absolute -right-0.5 -top-0.5 inline-flex min-h-[18px] min-w-[18px] items-center justify-center rounded-full bg-primary-500 px-1 text-[11px] font-bold text-white">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed left-3 right-3 top-[4.35rem] z-[70] max-h-[calc(100dvh-5.25rem)] overflow-hidden rounded-2xl border border-primary-100 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-900 sm:absolute sm:left-auto sm:right-0 sm:top-full sm:z-50 sm:mt-2 sm:max-h-none sm:w-[380px] sm:min-w-[320px]">
          <div className="border-b border-primary-100 dark:border-slate-800 px-4 py-3">
            <h3 className="text-base font-semibold leading-6 text-primary-900 dark:text-white">{t('notifications.title')}</h3>
            <p className="mt-1 text-xs leading-5 text-primary-600 dark:text-primary-300">
              {t('notifications.unreadCount', { count: unreadCount, suffix: unreadCount === 1 ? '' : 's' })}
            </p>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={() => void markAllAsRead()}
                className="mt-2 inline-flex max-w-full items-center gap-1 rounded-full px-2 py-1 text-xs font-medium leading-5 text-primary-600 transition-colors hover:bg-primary-50 dark:text-primary-300 dark:hover:bg-primary-900/20"
              >
                <CheckCheck size={14} className="flex-shrink-0" />
                <span className="break-words text-left">{t('notifications.markAllRead')}</span>
              </button>
            )}
          </div>

          {loading ? (
            <div className="p-2">
              <p className="text-sm font-medium text-primary-900 dark:text-white text-center py-4">{t('notifications.loading')}</p>
            </div>
          ) : previewItems.length === 0 ? (
            <div className="p-2">
              <p className="text-sm font-medium text-primary-900 dark:text-white text-center py-4">{t('notifications.emptyTitle')}</p>
              <p className="text-xs text-primary-600 dark:text-primary-300 text-center pb-2">{t('notifications.emptySubtitle')}</p>
            </div>
          ) : (
            <div className="p-2 max-h-[calc(100dvh-14rem)] overflow-y-auto overscroll-contain sm:max-h-[24rem]">
              {previewItems.map((notification) => (
                <button
                  key={notification.id}
                  type="button"
                  onClick={() => openNotification(notification)}
                  className={`flex w-full flex-col rounded-xl px-3 py-2.5 text-left text-sm transition-colors ${
                    notification.isRead ? 'bg-transparent text-primary-900 dark:text-white' : 'bg-primary-50/60 text-slate-900 dark:bg-primary-950/20 dark:text-slate-100'
                  } hover:bg-primary-50 dark:hover:bg-slate-800`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-semibold leading-tight flex-1">{notification.title}</p>
                    {!notification.isRead && <span className="mt-1 h-2 w-2 rounded-full bg-primary-500 flex-shrink-0" />}
                  </div>
                  <p className="mt-1 text-xs leading-5 text-primary-700 dark:text-primary-200 line-clamp-2">
                    {notification.message}
                  </p>
                  <p className="mt-2 text-[10px] font-medium uppercase tracking-[0.16em] text-primary-500 dark:text-primary-400">
                    {cacheUtils.getTimeAgo(notification.createdAt, locale, t)}
                  </p>
                </button>
              ))}
            </div>
          )}

          <div className="border-t border-primary-100 dark:border-slate-800 px-4 py-2.5">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                navigate('/notifications');
              }}
              className="w-full rounded-xl bg-primary-50 px-3 py-2.5 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-100 dark:bg-primary-900/30 dark:text-primary-300 dark:hover:bg-primary-900/50"
            >
              {t('notifications.viewAll')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
